import {
    useRepositoryMutation,
    useRepositorySuspenseQuery,
    PortfolioServiceType,
    PortfolioBookablePeriod,
    BookablePeriod,
    Service,
} from '@mews-commander/data-access';
import { showSuccessAlert, lt, useLocalizeProperty } from '@mews/framework';
import { getBookablePeriod } from '@mews-commander/utils';
import type {
    AddProductToServiceFormValues,
    UseAvailableServicesProps,
    UseAddProductToServiceMutationProps,
    AvailableServicesData,
} from './types';
import {
    createServiceOption,
    transformServicesWithIds,
    transformToServiceProducts,
    createUniqueServiceKey,
} from './utils';

const useServiceKeyCreation = () => {
    const localize = useLocalizeProperty();

    const convertToPortfolioBookablePeriod = (
        period: BookablePeriod | PortfolioBookablePeriod
    ): PortfolioBookablePeriod => {
        switch (period) {
            case BookablePeriod.Hour:
            case PortfolioBookablePeriod.Hour:
                return PortfolioBookablePeriod.Hour;
            case BookablePeriod.Day:
            case PortfolioBookablePeriod.Day:
                return PortfolioBookablePeriod.Day;
            case BookablePeriod.Night:
            case PortfolioBookablePeriod.Night:
                return PortfolioBookablePeriod.Night;
            case BookablePeriod.Month:
            case PortfolioBookablePeriod.Month:
                return PortfolioBookablePeriod.Month;
            default:
                return PortfolioBookablePeriod.Day;
        }
    };

    const createServiceKey = (service: Service, existingKeys: Set<string>) => {
        const bookablePeriod = getBookablePeriod(service);
        const baseKey = localize(service.names);
        const serviceType = PortfolioServiceType[service.data.discriminator as keyof typeof PortfolioServiceType];
        const uniqueKey = createUniqueServiceKey(baseKey, service.id, existingKeys);

        return {
            key: uniqueKey,
            serviceType,
            bookablePeriod: convertToPortfolioBookablePeriod(bookablePeriod),
        };
    };

    const createServiceKeyIdPair = (service: Service, uniqueKey: string) => ({
        [uniqueKey]: service.id,
    });

    return { createServiceKey, createServiceKeyIdPair };
};

export const useAvailableServices = ({
    propertyId,
    serviceIdsInUse,
}: UseAvailableServicesProps): AvailableServicesData => {
    const { createServiceKey, createServiceKeyIdPair } = useServiceKeyCreation();

    const {
        data: { serviceKeys, servicesList, serviceKeyIdPairs },
    } = useRepositorySuspenseQuery(
        'services',
        'getAll',
        { enterpriseIds: [propertyId] },
        {
            select: ({ services }) => {
                const availableServices = services.filter(({ id }) => !serviceIdsInUse.includes(id));

                // Handle duplicate service keys by creating unique keys with service type information
                const existingKeys = new Set<string>();
                const keys = availableServices.map((service) => {
                    const serviceKey = createServiceKey(service, existingKeys);
                    existingKeys.add(serviceKey.key);
                    return serviceKey;
                });

                const keyIdPairs = availableServices.map((service, index) =>
                    createServiceKeyIdPair(service, keys[index].key)
                );

                const options = keys.map(({ key }) => createServiceOption(key));

                return { serviceKeys: keys, servicesList: options, serviceKeyIdPairs: keyIdPairs };
            },
        }
    );

    return { serviceKeys, servicesList, serviceKeyIdPairs };
};

export const useAddProductToServiceMutation = ({
    enterpriseProductId,
    centralProductId,
    propertyId,
    serviceKeyIdPairs,
}: UseAddProductToServiceMutationProps) => {
    const { mutateAsync } = useRepositoryMutation('portfolioEnterpriseProducts', 'addServiceProducts', {
        onSuccess: () => showSuccessAlert({ title: lt('ChangesSaved') }),
        invalidateQueries: { portfolioEnterpriseProducts: ['getDetail'] },
    });

    const handleSubmit = async (formValues: AddProductToServiceFormValues): Promise<void> => {
        const { services } = formValues;

        if (!services) {
            return;
        }

        const servicesWithIds = transformServicesWithIds({ services, serviceKeyIdPairs });
        const serviceProducts = transformToServiceProducts(servicesWithIds);

        await mutateAsync({
            enterpriseProductId,
            centralProductId,
            propertyId,
            serviceProducts,
        });
    };

    return { handleSubmit };
};
